<template>
  <div
    class="app"
    :class="{
      'app--fullscreen': !store.state.isMenuOpen,
      [backgroundClass]: true,
    }"
  >
    <div class="app__content" v-if="store.state.currentUser !== undefined">
      <router-view />
    </div>
    <!-- Numéro de version (debug, temporaire) — discret en haut à gauche -->
    <span v-if="afficherVersion" class="app__version">v{{ appVersion }}</span>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { prefetchCollectionThumbs } from "@/utils/prefetchThumbs";

const store = useStore();
const route = useRoute();

// Dès que la liste des animaux est connue, préchauffe les vignettes de la
// grille en tâche de fond : la Collection s'ouvre sans attente (opt.4).
// Pas sur la landing « / » (27/09, CTO) : ses visiteurs sont surtout des
// parents pas encore joueurs, inutile de leur faire charger 51 images. Le
// préchauffage reprend dès qu'on entre dans l'app (route.name attend que le
// routeur ait résolu la 1re route).
watch(
  [() => store.state.animals, () => route.name],
  ([animals, routeName]) => {
    if (animals.length && routeName && routeName !== "landing-route") {
      prefetchCollectionThumbs(animals.map((a) => a.id));
    }
  }
);

// Injecté au build par Vite (voir vite.config.mjs define).
const appVersion = __APP_VERSION__;
// Badge HORS PROD seulement (27/09) : dev local, émulateurs et staging. Décidé
// par le projet Firebase du build (pas par le nom d'hôte) : le build de main
// vise animax-bl, et animax.eco ne l'affiche jamais.
const afficherVersion = import.meta.env.VITE_FIREBASE_PROJECT_ID !== "animax-bl";

const profileRoutes = [
  "profile-route",
  "friend-collection-route",
];

const backgroundClass = computed(() => {
  if (route.matched.some(({ name }) => name === "collection-route")) {
    return "app--collection";
  } else if (route.matched.some(({ name }) => name === "habitats-route")) {
    return "app--habitats";
  } else if (route.matched.some(({ name }) => profileRoutes.includes(name))) {
    return "app--profile";
  }
});
onMounted(() => {
  store.dispatch("fetchCurrentUser");
  store.dispatch("fetchAnimals");
  store.dispatch("fetchHabitats");
});
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: white;
  touch-action: none;
}

.app {
  height: 100vh;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(124deg, #c33f39 0%, #450223 37%, #17141f 100%);

  // Un seul fond par écran (les modules sont transparents, seule leur
  // bordure animée est dessinée) — déclinaisons dans _page-backgrounds.scss.
  &--collection {
    background: $bgGoldPage;
  }

  &--habitats {
    background: $bgRedPage;
  }

  &--profile {
    background: $bgPinkPage;
  }

  &--fullscreen {
    padding: 0;
  }

  // Badge de version (debug, à retirer plus tard) : discret, par-dessus tout,
  // non cliquable, en haut à gauche (le header avatar est en haut à droite).
  &__version {
    position: fixed;
    top: calc(3px + env(safe-area-inset-top));
    // Décalé pour ne pas chevaucher le bouton home (en haut à gauche).
    left: 52px;
    z-index: 9999;
    font-family: sans-serif;
    font-size: 8px;
    line-height: 1;
    color: #fff;
    pointer-events: none;
    user-select: none;
  }

  &__content {
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    height: 100%;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
